import React from 'react';
import Modal from '../../../components/base/Modal';
import ViewModeToggle from '../../../components/common/ViewModeToggle';
import InputLabel from '../../../components/base/InputLabel';
import NumberInput from '../../../components/base/NumberInput';
import MultiTpSettings from '../../../components/metrics/MultiTpSettings';
import BigNumber from 'bignumber.js';

// Default fee breakdown structure (per side)
const DEFAULT_FEE_BREAKDOWN = {
  broker: 2.00,      // Broker commission per side
  exchange: 1.32,    // Exchange fee per side
  nfa: 0.02,         // NFA fee per side
  clearing: 0.20,    // Clearing fee per side
};

const FuturesSettingsModal = ({ isOpen, onClose, values, setValues, initialFocusField, viewMode, onViewModeChange }) => {
  // Calculate total round turn fee from breakdown
  const calculateTotalFee = () => {
    const { broker, exchange, nfa, clearing } = DEFAULT_FEE_BREAKDOWN;
    return (broker + exchange + nfa + clearing) * 2;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Settings"
    >
      <div className="space-y-6 pb-4">
        {/* View mode — Full / Compact (moved here from the metrics card) */}
        <div className="flex items-center justify-between">
          <InputLabel>View</InputLabel>
          <ViewModeToggle viewMode={viewMode} onChange={onViewModeChange} />
        </div>

        {/* Risk and Account Balance */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Risk</InputLabel>
              <div className="flex gap-1">
                <button
                  onClick={() => setValues({ riskUnit: 'percent' })}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                    values.riskUnit === 'percent'
                      ? 'bg-primary-orange/20 text-primary-orange'
                      : 'hover:bg-slate-800 text-slate-500'
                  }`}
                >
                  %
                </button>
                <button
                  onClick={() => setValues({ riskUnit: 'fixed' })}
                  className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
                    values.riskUnit === 'fixed'
                      ? 'bg-primary-orange/20 text-primary-orange'
                      : 'hover:bg-slate-800 text-slate-500'
                  }`}
                >
                  $
                </button>
              </div>
            </div>
            <NumberInput
              value={values.risk}
              onChange={(value) => setValues({ risk: value || new BigNumber(0) })}
              suffix={values.riskUnit === 'percent' ? '%' : undefined}
              prefix={values.riskUnit === 'fixed' ? '$' : undefined}
              decimals={2}
              required
              min={0.01}
            />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <InputLabel required>Account Balance</InputLabel>
              <div className="h-[28px]"></div>
            </div>
            <NumberInput
              value={values.accountBalance}
              onChange={(value) => setValues({ accountBalance: value || new BigNumber(0) })}
              prefix="$"
              decimals={2}
              required
              min={0.01}
              autoFocus={initialFocusField === 'accountBalance'}
            />
          </div>
        </div>

        {/* R:R and Round Turn Fee */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <InputLabel required>Desired R:R</InputLabel>
            <NumberInput
              value={values.desiredRR}
              onChange={(value) => setValues({ desiredRR: value || new BigNumber(0) })}
              suffix="x"
              decimals={1}
              required
              min={0.1}
            />
          </div>

          <div>
            <InputLabel required>Round Turn Fee</InputLabel>
            <NumberInput
              value={values.roundTurnFee}
              onChange={(value) => setValues({ roundTurnFee: value || new BigNumber(0) })}
              prefix="$"
              decimals={2}
              required
              min={0}
              autoFocus={initialFocusField === 'roundTurnFee'}
            />
          </div>
        </div>

        {/* Risk Overshoot Tolerance - Variant C: Compact Setting Row */}
        <div className="flex items-center justify-between py-3 border-b border-slate-700/50 bg-purple-500/5 -mx-6 px-6 rounded">
          <div>
            <div className="text-slate-300 text-sm flex items-center gap-2">
              Risk Overshoot Tolerance
              <span className="text-purple-400 text-xs bg-purple-500/20 px-1.5 py-0.5 rounded">New</span>
            </div>
            <div className="text-slate-500 text-xs">Allow larger position if overshoot within this %</div>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={values.riskOvershootTolerance || 0}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 0 && val <= 100) {
                  setValues({ riskOvershootTolerance: val });
                }
              }}
              className="w-16 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-right text-white focus:outline-none focus:border-purple-500/50"
            />
            <span className="text-slate-500">%</span>
          </div>
        </div>

        {/* Fee Breakdown (read-only display) */}
        <div className="pt-2">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-slate-400 text-xs uppercase tracking-wider font-medium">Fee Breakdown</span>
            <div className="h-px flex-1 bg-white/5" />
          </div>
          <div className="bg-slate-800/40 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Broker Commission</span>
              <span className="text-slate-400">${DEFAULT_FEE_BREAKDOWN.broker.toFixed(2)}/side × 2 = ${(DEFAULT_FEE_BREAKDOWN.broker * 2).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Exchange Fee</span>
              <span className="text-slate-400">${DEFAULT_FEE_BREAKDOWN.exchange.toFixed(2)}/side × 2 = ${(DEFAULT_FEE_BREAKDOWN.exchange * 2).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">NFA Fee</span>
              <span className="text-slate-400">${DEFAULT_FEE_BREAKDOWN.nfa.toFixed(2)}/side × 2 = ${(DEFAULT_FEE_BREAKDOWN.nfa * 2).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Clearing Fee</span>
              <span className="text-slate-400">${DEFAULT_FEE_BREAKDOWN.clearing.toFixed(2)}/side × 2 = ${(DEFAULT_FEE_BREAKDOWN.clearing * 2).toFixed(2)}</span>
            </div>
            <div className="pt-2 border-t border-white/5 flex justify-between text-xs">
              <span className="text-slate-400 font-medium">Total Round Turn</span>
              <span className="text-amber-400 font-medium">${calculateTotalFee().toFixed(2)}/contract</span>
            </div>
          </div>
          <p className="text-slate-500 text-xs mt-2">
            You can adjust the Round Turn Fee above if your broker has different rates.
          </p>
        </div>

        <MultiTpSettings values={values} onChange={(p) => setValues(p)} />
      </div>
    </Modal>
  );
};

export default FuturesSettingsModal;
